/**
 * PhotoBank — AK-257 фотобанк + линзы-культуры для редактора болезни/
 * вредителя/сорняка. Реализует approved wireframe v2
 * (docs/superpowers/prototypes/ak256-per-culture-images-v2/index.html, B/C/D/E).
 *
 * Ментальная модель (founder-reject v1 «тегирование поштучно»):
 *   ПУЛ фото — отдельно (загрузка), ПРИВЯЗКА к культурам — отдельно (линзы / bulk).
 *
 * Режимы:
 *   • «Все фото» (allview) — обзор: под фото цветные точки культур, ⛓N если общее.
 *   • Линза-культура — клик-toggle: фото в линзе с галкой/рамкой, остальные dimmed;
 *     клик по фото = привязать/отвязать к этой культуре.
 *   • Мульти-выбор → «Привязать к культурам…» — bulk-assign диалог (Q4).
 *
 * Инварианты (зеркалят backend):
 *   • D1 — у фото всегда ≥1 культура. Отвязка последней культуры через линзу =
 *     удаление фото (confirm). Bulk-диалог не даёт применить пустой набор.
 *   • crop_not_affected — привязывать можно только к культурам из «Поражаемых»
 *     (линзы и чекбоксы показывают только их; сервер дублирует проверку).
 *
 * DS-first: Button/Badge/Dialog/Skeleton/Checkbox из @/components/ui/*.
 * Цвета культур — getCropColor (whitelisted domain palette).
 */
import { useMemo, useRef, useState } from 'react';
import {
  Check,
  ImagePlus,
  Link2,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import { getCropColor } from '@/lib/colorMaps/cropColors';
import type {
  AffectedCrop,
  ImageDict,
  ReferenceImage,
} from '@/pages/platform-admin/reference/types';
import {
  useDeleteReferenceImage,
  useReferenceImages,
  useUpdateImageCrops,
  useUploadReferenceImage,
} from '@/pages/platform-admin/reference/hooks/useImageBankQueries';

const ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX_BYTES = 10 * 1024 * 1024;

export interface PhotoBankProps {
  dict: ImageDict;
  entityId: string;
  affectedCrops: AffectedCrop[];
}

type Lens = 'all' | string; // 'all' | crop_id

export function PhotoBank({ dict, entityId, affectedCrops }: PhotoBankProps) {
  const imagesQ = useReferenceImages(dict, entityId);
  const uploadMut = useUploadReferenceImage(dict);
  const updateCropsMut = useUpdateImageCrops(dict);
  const deleteMut = useDeleteReferenceImage(dict);

  const [lens, setLens] = useState<Lens>('all');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const images = imagesQ.data ?? [];
  const cropName = (id: string) =>
    affectedCrops.find((c) => c.crop_id === id)?.name ?? '—';

  // Counts per lens chip.
  const countFor = (cropId: string) =>
    images.filter((im) => im.crop_ids.includes(cropId)).length;

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (affectedCrops.length === 0) {
      toast.error('Сначала добавьте поражаемые культуры.');
      return;
    }
    // D1: a freshly-uploaded photo needs ≥1 crop. If a single-crop lens is
    // active, attach to it; otherwise attach to ALL affected crops by default
    // (admin then narrows via lenses). This keeps "loading ≠ tagging" but never
    // creates an orphan.
    const defaultCrops =
      lens !== 'all' ? [lens] : affectedCrops.map((c) => c.crop_id);
    for (const file of Array.from(files)) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: больше 10 МБ.`);
        continue;
      }
      try {
        await uploadMut.mutateAsync({ entityId, file, cropIds: defaultCrops });
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.detail : `Не удалось загрузить ${file.name}`,
        );
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  // Click-toggle a photo's membership in the active crop lens.
  const toggleInLens = async (img: ReferenceImage) => {
    if (lens === 'all') return;
    const inLens = img.crop_ids.includes(lens);
    const next = inLens
      ? img.crop_ids.filter((c) => c !== lens)
      : [...img.crop_ids, lens];
    if (next.length === 0) {
      // D1 — removing the last crop = delete the photo (confirm).
      if (
        !window.confirm(
          'Это единственная культура у фото. Снять привязку = удалить фото. Продолжить?',
        )
      ) {
        return;
      }
      try {
        await deleteMut.mutateAsync({ entityId, imageId: img.id });
      } catch (err) {
        toast.error(err instanceof ApiError ? err.detail : 'Не удалось удалить фото');
      }
      return;
    }
    try {
      await updateCropsMut.mutateAsync({ entityId, imageId: img.id, cropIds: next });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : 'Не удалось изменить привязку');
    }
  };

  const handleDelete = async (img: ReferenceImage) => {
    if (!window.confirm('Удалить фото из пула?')) return;
    try {
      await deleteMut.mutateAsync({ entityId, imageId: img.id });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : 'Не удалось удалить фото');
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  if (imagesQ.isLoading) {
    return (
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square w-full rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Lens bar */}
      <div className="mb-3 flex flex-nowrap gap-2 overflow-x-auto pb-1">
        <LensChip
          label="Все фото"
          count={images.length}
          active={lens === 'all'}
          onClick={() => setLens('all')}
        />
        {affectedCrops.map((c) => (
          <LensChip
            key={c.crop_id}
            label={c.name ?? '—'}
            color={getCropColor(c.name).border}
            count={countFor(c.crop_id)}
            active={lens === c.crop_id}
            onClick={() => setLens(c.crop_id)}
          />
        ))}
      </div>

      {/* Active-lens helper */}
      {lens === 'all' ? (
        <p className="mb-3 rounded-md bg-secondary/70 px-3 py-2 text-xs text-muted-foreground">
          Обзор: точки под фото показывают привязку к культурам. Выберите культуру
          выше, чтобы редактировать её привязки.
        </p>
      ) : (
        <p className="mb-3 flex items-center gap-2 rounded-md bg-primary/[0.06] px-3 py-2 text-xs text-foreground">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: getCropColor(cropName(lens)).border }}
          />
          <span>
            <b className="font-semibold">{cropName(lens)}:</b> кликните фото, чтобы
            привязать / отвязать. {countFor(lens)} из {images.length} привязано.
          </span>
        </p>
      )}

      {/* Toolbar */}
      <div className="mb-2.5 flex items-center gap-2">
        <span className="text-sm font-semibold">Пул фото</span>
        <span className="text-xs text-muted-foreground">{images.length} шт</span>
        <span className="ml-auto" />
        {selectMode ? (
          <Button variant="outline" size="sm" onClick={exitSelect}>
            Снять выбор
          </Button>
        ) : (
          images.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectMode(true)}
            >
              <Check className="h-3.5 w-3.5" />
              Выбрать
            </Button>
          )
        )}
        <Button
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploadMut.isPending}
        >
          {uploadMut.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          Загрузить
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => void handleUpload(e.target.files)}
        />
      </div>

      {/* Grid */}
      {images.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border px-4 py-7 text-center">
          <ImagePlus className="h-7 w-7 text-muted-foreground" />
          <div className="text-sm font-semibold text-foreground">
            Загрузите фото в пул
          </div>
          <p className="max-w-sm text-xs text-muted-foreground">
            Сначала добавьте фото в банк — привязку к культурам сделаете отдельным
            шагом (линзы / мульти-выбор).
          </p>
          <Button
            size="sm"
            className="mt-1"
            onClick={() => fileRef.current?.click()}
            disabled={uploadMut.isPending}
          >
            <Plus className="h-3.5 w-3.5" />
            Загрузить фото
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {images.map((img) => (
            <PhotoTile
              key={img.id}
              img={img}
              lens={lens}
              affectedCrops={affectedCrops}
              selectMode={selectMode}
              selected={selected.has(img.id)}
              onToggleSelect={() => toggleSelect(img.id)}
              onClickInLens={() => void toggleInLens(img)}
              onDelete={() => void handleDelete(img)}
            />
          ))}
        </div>
      )}

      {/* Bulk-assign bar */}
      {selectMode && selected.size > 0 && (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/[0.08] px-3 py-2.5">
          <div>
            <div className="text-sm font-semibold text-primary">
              {selected.size} фото выбрано
            </div>
            <div className="text-xs text-muted-foreground">
              Привязать к культурам разом (Q4)
            </div>
          </div>
          <span className="ml-auto" />
          <Button size="sm" onClick={() => setAssignOpen(true)}>
            <Link2 className="h-3.5 w-3.5" />
            Привязать к культурам…
          </Button>
        </div>
      )}

      <BulkAssignDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        affectedCrops={affectedCrops}
        selectedImages={images.filter((im) => selected.has(im.id))}
        onApply={async (cropIds) => {
          for (const img of images.filter((im) => selected.has(im.id))) {
            // Union of existing crops + newly-checked crops, restricted to the
            // explicit checked set (checkbox semantics: checked = should-have).
            try {
              await updateCropsMut.mutateAsync({
                entityId,
                imageId: img.id,
                cropIds,
              });
            } catch (err) {
              toast.error(
                err instanceof ApiError ? err.detail : 'Не удалось привязать',
              );
            }
          }
          setAssignOpen(false);
          exitSelect();
        }}
      />
    </div>
  );
}

// ---------- LensChip ----------

interface LensChipProps {
  label: string;
  count: number;
  active: boolean;
  color?: string;
  onClick: () => void;
}

function LensChip({ label, count, active, color, onClick }: LensChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium',
        active
          ? 'border-primary bg-primary/[0.08] text-primary ring-1 ring-inset ring-primary/35'
          : 'border-border bg-background text-foreground hover:bg-accent',
      )}
    >
      {color && (
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ background: color }}
        />
      )}
      {label}
      <span
        className={cn(
          'rounded-full px-1.5 text-[10px] font-semibold',
          active
            ? 'bg-primary/[0.18] text-primary'
            : 'bg-secondary text-muted-foreground',
        )}
      >
        {count}
      </span>
    </button>
  );
}

// ---------- PhotoTile ----------

interface PhotoTileProps {
  img: ReferenceImage;
  lens: Lens;
  affectedCrops: AffectedCrop[];
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onClickInLens: () => void;
  onDelete: () => void;
}

function PhotoTile({
  img,
  lens,
  affectedCrops,
  selectMode,
  selected,
  onToggleSelect,
  onClickInLens,
  onDelete,
}: PhotoTileProps) {
  const inLens = lens !== 'all' && img.crop_ids.includes(lens);
  const outLens = lens !== 'all' && !inLens;
  const shared = img.crop_ids.length > 1;

  const handleClick = () => {
    if (selectMode) onToggleSelect();
    else if (lens !== 'all') onClickInLens();
  };

  return (
    <div
      className={cn(
        'group relative aspect-square cursor-pointer overflow-hidden rounded-md border border-border bg-muted',
        (inLens || selected) && 'ring-2 ring-inset ring-primary',
      )}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <img src={img.url} alt="" className="h-full w-full object-cover" />

      {/* dim photos not in active lens */}
      {outLens && <div className="absolute inset-0 bg-background/55" />}

      {/* in-lens check */}
      {inLens && (
        <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
          <Check className="h-3 w-3" />
        </span>
      )}

      {/* shared-cultures badge */}
      {shared && lens === 'all' && (
        <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-accent-brand-strong px-1.5 py-0.5 text-[10px] font-semibold text-white shadow">
          <Link2 className="h-2.5 w-2.5" />
          {img.crop_ids.length}
        </span>
      )}

      {/* culture dots (allview) */}
      {lens === 'all' && (
        <div className="absolute bottom-1.5 left-1.5 flex gap-0.5">
          {img.crop_ids.map((cid) => {
            const nm = affectedCrops.find((c) => c.crop_id === cid)?.name;
            return (
              <span
                key={cid}
                className="h-1.5 w-1.5 rounded-full ring-1 ring-white/85"
                style={{ background: getCropColor(nm).border }}
                title={nm ?? undefined}
              />
            );
          })}
        </div>
      )}

      {/* multi-select tick */}
      {selectMode && (
        <span
          className={cn(
            'absolute bottom-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-sm border',
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-white/90 bg-background/35',
          )}
        >
          {selected && <Check className="h-3 w-3" />}
        </span>
      )}

      {/* delete (hover, allview only — avoids clash with lens click) */}
      {!selectMode && lens === 'all' && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute right-1.5 top-1.5 hidden h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow group-hover:flex"
          aria-label="Удалить фото"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// ---------- BulkAssignDialog ----------

interface BulkAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  affectedCrops: AffectedCrop[];
  selectedImages: ReferenceImage[];
  onApply: (cropIds: string[]) => Promise<void>;
}

function BulkAssignDialog({
  open,
  onOpenChange,
  affectedCrops,
  selectedImages,
  onApply,
}: BulkAssignDialogProps) {
  // Pre-check crops that ALL selected images already have.
  const initialChecked = useMemo(() => {
    if (selectedImages.length === 0) return new Set<string>();
    const common = affectedCrops
      .map((c) => c.crop_id)
      .filter((cid) => selectedImages.every((im) => im.crop_ids.includes(cid)));
    return new Set(common);
  }, [selectedImages, affectedCrops]);

  const [checked, setChecked] = useState<Set<string>>(initialChecked);
  const [applying, setApplying] = useState(false);

  // Re-seed when dialog opens with a new selection.
  const seedKey = selectedImages.map((i) => i.id).join(',');
  const seededRef = useRef('');
  if (open && seededRef.current !== seedKey) {
    seededRef.current = seedKey;
    setChecked(new Set(initialChecked));
  }

  const toggle = (cid: string) => {
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const apply = async () => {
    setApplying(true);
    try {
      await onApply(Array.from(checked));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Привязать к культурам</DialogTitle>
        </DialogHeader>
        <p className="-mt-1 text-sm text-muted-foreground">
          {selectedImages.length} фото будут привязаны к отмеченным культурам.
        </p>
        <div className="flex flex-col gap-0.5 py-1">
          {affectedCrops.map((c) => (
            <button
              key={c.crop_id}
              type="button"
              onClick={() => toggle(c.crop_id)}
              className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm hover:bg-secondary/60"
            >
              <Checkbox checked={checked.has(c.crop_id)} className="pointer-events-none" />
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: getCropColor(c.name).border }}
              />
              <span className="flex-1">{c.name ?? '—'}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-accent-brand-strong">
          Снятая галка = отвязать. ≥1 культура обязательна (D1).
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
            Отмена
          </Button>
          <Button onClick={() => void apply()} disabled={applying || checked.size === 0}>
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Применить к {selectedImages.length} фото
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PhotoBank;
